import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  format,
  startOfWeek,
  startOfMonth,
  startOfYear,
  endOfWeek,
  endOfMonth,
  endOfYear,
  parseISO,
} from "date-fns";
import {
  DollarSign,
  Banknote,
  Gift,
  Wallet,
  AlertTriangle,
  Calendar,
  Download,
  Printer,
  TrendingUp,
  Info,
  Ticket,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const fmtMoneySigned = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  if (v < 0) return `(${abs})`;
  return abs;
};

const today = () => new Date();
const iso = (d) => format(d, "yyyy-MM-dd");

const RANGE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
  { key: "custom", label: "Custom" },
];

const presetRange = (key) => {
  const now = today();
  switch (key) {
    case "today":
      return { start: iso(now), end: iso(now) };
    case "week":
      return { start: iso(startOfWeek(now, { weekStartsOn: 1 })), end: iso(endOfWeek(now, { weekStartsOn: 1 })) };
    case "month":
      return { start: iso(startOfMonth(now)), end: iso(endOfMonth(now)) };
    case "year":
      return { start: iso(startOfYear(now)), end: iso(endOfYear(now)) };
    default:
      return { start: iso(startOfMonth(now)), end: iso(endOfMonth(now)) };
  }
};

// KPI tile -----------------------------------------------------------------
function KPI({ icon: Icon, label, value, subtitle, accent = "text-primary", testId }) {
  return (
    <Card className="border-border/50" data-testid={testId}>
      <CardContent className="p-4 flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg bg-secondary/40 flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${accent}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground leading-tight">{label}</p>
          <p className={`font-mono font-bold text-xl tabular-nums leading-tight mt-0.5 ${accent}`}>{value}</p>
          {subtitle && <p className="text-xs font-mono text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// Donut for prize breakdown (CSS conic-gradient — no extra chart container needed)
function PrizeDonut({ data, total }) {
  const colors = {
    online: "#10b981",
    instant: "#f59e0b",
    credit_card: "#3b82f6",
    coupon: "#a855f7",
  };
  const labels = {
    online: "Online",
    instant: "Instant",
    credit_card: "Credit Card",
    coupon: "Coupons/Others",
  };
  const entries = Object.entries(data).filter(([, v]) => v > 0);
  if (!entries.length) {
    return (
      <div className="flex items-center justify-center h-44 font-mono text-xs text-muted-foreground">
        No prizes paid in range
      </div>
    );
  }
  let acc = 0;
  const segments = entries.map(([k, v]) => {
    const pct = (v / total) * 100;
    const seg = `${colors[k]} ${acc}% ${acc + pct}%`;
    acc += pct;
    return seg;
  });
  const gradient = `conic-gradient(${segments.join(", ")})`;
  return (
    <div className="flex items-center gap-5">
      <div className="relative w-32 h-32 flex-shrink-0">
        <div
          className="absolute inset-0 rounded-full"
          style={{ background: gradient }}
        />
        <div className="absolute inset-3 rounded-full bg-card flex items-center justify-center flex-col">
          <span className="text-[10px] font-mono text-muted-foreground">Total</span>
          <span className="font-mono font-bold text-card-foreground text-sm">{fmtMoney(total)}</span>
        </div>
      </div>
      <div className="space-y-1.5">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-xs font-mono">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colors[k] }} />
            <span className="text-muted-foreground">{labels[k]}</span>
            <span className="font-bold text-card-foreground ml-auto">{fmtMoney(v)}</span>
            <span className="text-muted-foreground tabular-nums">({((v / total) * 100).toFixed(0)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// CSV download helper ------------------------------------------------------
const downloadCSV = (filename, rows) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const lines = [headers.join(",")];
  rows.forEach((r) => {
    lines.push(headers.map((h) => {
      const v = r[h] ?? "";
      const s = String(v);
      return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// History Totals card ------------------------------------------------------
const HISTORY_PERIODS = [
  { key: "week", label: "Weekly" },
  { key: "month", label: "Monthly" },
  { key: "year", label: "Yearly" },
  { key: "custom", label: "Custom" },
];

function HistoryTotals({ onJumpToRange }) {
  const [period, setPeriod] = useState("month");
  const [customStart, setCustomStart] = useState(iso(startOfMonth(today())));
  const [customEnd, setCustomEnd] = useState(iso(endOfMonth(today())));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ entries_count: 0, range: { start: "", end: "" } });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom") {
        params.start = customStart;
        params.end = customEnd;
      }
      const res = await axios.get(`${API}/lvm/stats/history`, { params });
      setRows(res.data.rows || []);
      setMeta({ entries_count: res.data.entries_count, range: res.data.range });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load history totals");
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const exportRows = () => {
    if (!rows.length) { toast.error("Nothing to export"); return; }
    downloadCSV(`lvm_history_${period}_${meta.range.start}_to_${meta.range.end}.csv`, rows.map((r) => ({
      period: r.label,
      start: r.start,
      end: r.end,
      entries: r.entries_count,
      sales: r.sales,
      cash: r.cash,
      prizes: r.prizes,
      net_cash: r.net_cash,
      mismatch_days: r.mismatch_days,
      mismatch_delta: r.mismatch_delta,
    })));
    toast.success("CSV exported");
  };

  return (
    <Card className="border-border/50" data-testid="history-totals-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-mono text-sm flex items-center gap-2 flex-wrap">
          <Calendar className="w-4 h-4 text-primary" />
          History Totals
          <div className="flex items-center gap-1 ml-2">
            {HISTORY_PERIODS.map((p) => (
              <Button
                key={p.key}
                variant={period === p.key ? "default" : "outline"}
                size="sm"
                onClick={() => setPeriod(p.key)}
                className={`h-7 px-2.5 text-xs font-mono ${period === p.key ? "btn-primary" : "btn-3d"}`}
                data-testid={`history-period-${p.key}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <span className="ml-auto flex items-center gap-1">
            <Button onClick={exportRows} variant="outline" size="sm" className="btn-3d h-7 px-2" data-testid="history-export-csv">
              <Download className="w-3.5 h-3.5 mr-1" />
              <span className="text-xs">CSV</span>
            </Button>
          </span>
        </CardTitle>
        {period === "custom" && (
          <div className="flex items-center gap-1.5 mt-2 font-mono text-xs" data-testid="history-custom-inputs">
            <Input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="h-8 w-36"
              data-testid="history-custom-start"
            />
            <span className="text-muted-foreground">→</span>
            <Input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="h-8 w-36"
              data-testid="history-custom-end"
            />
          </div>
        )}
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {loading ? (
          <p className="font-mono text-xs text-muted-foreground text-center py-4">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="font-mono text-xs text-muted-foreground text-center py-4">No entries in range</p>
        ) : (
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border/40">
                <th className="py-1.5 pr-2">Period</th>
                <th className="py-1.5 px-2 text-right">Entries</th>
                <th className="py-1.5 px-2 text-right">Sales</th>
                <th className="py-1.5 px-2 text-right">Cash</th>
                <th className="py-1.5 px-2 text-right">Prizes</th>
                <th className="py-1.5 px-2 text-right">Net Cash</th>
                <th className="py-1.5 pl-2 text-right">Mismatch</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.key}
                  className="border-b border-border/20 hover:bg-secondary/30 cursor-pointer"
                  onClick={() => onJumpToRange?.(r.start, r.end)}
                  title="Click to load this range above"
                  data-testid={`history-row-${r.key}`}
                >
                  <td className="py-1.5 pr-2 text-card-foreground">{r.label}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums">{r.entries_count}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-emerald-400">{fmtMoney(r.sales)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-amber-400">{fmtMoney(r.cash)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-rose-400">{fmtMoney(r.prizes)}</td>
                  <td className={`py-1.5 px-2 text-right tabular-nums ${r.net_cash < 0 ? "text-rose-400" : "text-primary"}`}>{fmtMoney(r.net_cash)}</td>
                  <td className={`py-1.5 pl-2 text-right tabular-nums ${r.mismatch_days > 0 ? "text-rose-400" : "text-muted-foreground"}`}>
                    {r.mismatch_days > 0 ? `${r.mismatch_days} d · ${fmtMoneySigned(r.mismatch_delta)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

// Main Dashboard component -------------------------------------------------
export default function LVMDashboard({ onJumpToDate }) {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(presetRange("month"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const onPreset = (key) => {
    setPreset(key);
    if (key !== "custom") setRange(presetRange(key));
  };

  const onCustomChange = (field, value) => {
    setPreset("custom");
    setRange((r) => ({ ...r, [field]: value }));
  };

  const fetchData = useCallback(async () => {
    if (!range.start || !range.end) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/lvm/stats/dashboard`, {
        params: { start: range.start, end: range.end },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load dashboard");
      toast.error("Failed to load LVM dashboard");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Chart data ----------------------------------------------------------
  const chartData = useMemo(() => {
    if (!data?.daily) return [];
    return data.daily.map((d) => ({
      date: format(parseISO(d.date), "MMM dd"),
      raw: d.date,
      "LVM-1": d.lvm1_sales,
      "LVM-2": d.lvm2_sales,
      Total: d.sales,
    }));
  }, [data]);

  const kpis = data?.kpis || { total_sales: 0, total_cash: 0, total_prizes: 0, net_cash: 0, mismatch_days: 0, mismatch_delta: 0, entries_count: 0 };

  const exportCSV = () => {
    if (!data?.daily?.length) {
      toast.error("Nothing to export — no entries in range");
      return;
    }
    downloadCSV(`lvm_dashboard_${range.start}_to_${range.end}.csv`, data.daily.map((d) => ({
      date: d.date,
      total_sales: d.sales,
      lvm1_sales: d.lvm1_sales,
      lvm2_sales: d.lvm2_sales,
      total_cash: d.cash,
      cash_in_hand: d.cash_in_hand,
      delta: d.delta,
      prizes_paid: d.prizes,
      mismatch: d.mismatch ? "YES" : "",
    })));
    toast.success("CSV exported");
  };

  const printDashboard = () => {
    // Use the browser's native print; CSS @media print rules in App.css hide chrome.
    window.print();
  };

  return (
    <div className="space-y-4" data-testid="lvm-dashboard">
      {/* Range toolbar */}
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <div className="flex items-center gap-1 flex-wrap">
          {RANGE_PRESETS.map((p) => (
            <Button
              key={p.key}
              variant={preset === p.key ? "default" : "outline"}
              size="sm"
              onClick={() => onPreset(p.key)}
              className={preset === p.key ? "btn-primary" : "btn-3d"}
              data-testid={`range-${p.key}`}
            >
              {p.label}
            </Button>
          ))}
        </div>

        {preset === "custom" && (
          <div className="flex items-center gap-1.5 ml-1 font-mono text-xs" data-testid="range-custom-inputs">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <Input
              type="date"
              value={range.start}
              onChange={(e) => onCustomChange("start", e.target.value)}
              className="h-8 w-36"
              data-testid="range-start"
            />
            <span className="text-muted-foreground">→</span>
            <Input
              type="date"
              value={range.end}
              onChange={(e) => onCustomChange("end", e.target.value)}
              className="h-8 w-36"
              data-testid="range-end"
            />
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          <Button onClick={exportCSV} variant="outline" size="sm" className="btn-3d" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" />
            <span className="text-xs">Export CSV</span>
          </Button>
          <Button onClick={printDashboard} variant="outline" size="sm" className="btn-3d" data-testid="print-dashboard-btn">
            <Printer className="w-4 h-4 mr-1" />
            <span className="text-xs">Print</span>
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center p-12 text-muted-foreground font-mono text-sm" data-testid="dashboard-loading">
          Loading LVM dashboard…
        </div>
      )}
      {error && !loading && (
        <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/40 text-destructive font-mono text-sm" data-testid="dashboard-error">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Range summary line */}
          <div className="font-mono text-xs text-muted-foreground" data-testid="range-summary">
            {range.start} → {range.end} · {kpis.entries_count} {kpis.entries_count === 1 ? "entry" : "entries"}
          </div>

          {/* Inventory caveat — actual stock can vary because pack sizes differ per denomination */}
          <div
            className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 font-mono text-xs text-amber-200"
            data-testid="inventory-note"
          >
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-400" />
            <span>
              <span className="font-bold">Please Note:</span> Actual inventory amounts can vary because some games (e.g. $50) come in different pack quantities.
            </span>
          </div>

          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kpi-grid">
            <KPI
              icon={DollarSign}
              label="Total Sales"
              value={fmtMoney(kpis.total_sales)}
              subtitle="O/L + Instant"
              accent="text-emerald-400"
              testId="kpi-total-sales"
            />
            <KPI
              icon={Banknote}
              label="Total Cash"
              value={fmtMoney(kpis.total_cash)}
              subtitle="From bill counts"
              accent="text-amber-400"
              testId="kpi-total-cash"
            />
            <KPI
              icon={Gift}
              label="Total Prizes Paid"
              value={fmtMoney(kpis.total_prizes)}
              subtitle="Online + Instant + CC + Coupon"
              accent="text-rose-400"
              testId="kpi-total-prizes"
            />
            <KPI
              icon={Ticket}
              label="Coupons/Others"
              value={fmtMoney(data?.prize_breakdown?.coupon || 0)}
              subtitle="Coupons + Others paid"
              accent="text-purple-400"
              testId="kpi-coupons"
            />
            <KPI
              icon={AlertTriangle}
              label="Mismatch Days"
              value={`${kpis.mismatch_days}`}
              subtitle={`Δ ${fmtMoneySigned(kpis.mismatch_delta)}`}
              accent={kpis.mismatch_days > 0 ? "text-rose-400" : "text-muted-foreground"}
              testId="kpi-mismatch"
            />
          </div>

          {/* Chart + Donut row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="border-border/50 lg:col-span-2" data-testid="sales-trend-card">
              <CardHeader className="pb-2">
                <CardTitle className="font-mono text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  Sales Trend (LVM-1 vs LVM-2)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {chartData.length === 0 ? (
                  <div className="flex items-center justify-center h-64 font-mono text-xs text-muted-foreground">
                    No entries in this range
                  </div>
                ) : (
                  <div className="w-full h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "monospace" }}
                          axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                        />
                        <YAxis
                          tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "monospace" }}
                          axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                          tickFormatter={(v) => `$${(v / 1000).toFixed(v >= 1000 ? 0 : 1)}k`}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "hsl(148, 25%, 16%)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 8,
                            fontFamily: "monospace",
                            fontSize: 12,
                          }}
                          formatter={(v) => fmtMoney(v)}
                          labelStyle={{ color: "#cbd5e1" }}
                          cursor={{ stroke: "rgba(74,222,128,0.4)", strokeWidth: 1 }}
                        />
                        <Legend
                          wrapperStyle={{ fontFamily: "monospace", fontSize: 11, paddingTop: 4 }}
                          iconType="circle"
                        />
                        <Line type="monotone" dataKey="LVM-1" stroke="#4ade80" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="LVM-2" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="Total" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border/50" data-testid="prize-donut-card">
              <CardHeader className="pb-2">
                <CardTitle className="font-mono text-sm flex items-center gap-2">
                  <Gift className="w-4 h-4 text-rose-400" />
                  Prizes Paid by Channel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PrizeDonut data={data?.prize_breakdown || {}} total={kpis.total_prizes} />
              </CardContent>
            </Card>
          </div>

          {/* Daily history + sub-lists */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Card className="border-border/50" data-testid="daily-history-card">
              <CardHeader className="pb-2">
                <CardTitle className="font-mono text-sm">Daily History</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {data?.daily?.length === 0 ? (
                  <p className="font-mono text-xs text-muted-foreground text-center py-6">No entries in this range</p>
                ) : (
                  <table className="w-full text-xs font-mono">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border/40">
                        <th className="py-1.5 pr-2">Date</th>
                        <th className="py-1.5 px-2 text-right">Sales</th>
                        <th className="py-1.5 px-2 text-right">Cash</th>
                        <th className="py-1.5 px-2 text-right">Prizes</th>
                        <th className="py-1.5 pl-2 text-right">Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.daily.slice().reverse().map((row) => (
                        <tr
                          key={row.date}
                          className="border-b border-border/20 hover:bg-secondary/30 cursor-pointer"
                          onClick={() => onJumpToDate?.(row.date)}
                          data-testid={`daily-row-${row.date}`}
                        >
                          <td className="py-1.5 pr-2">{format(parseISO(row.date), "MMM dd, yyyy")}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{fmtMoney(row.sales)}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{fmtMoney(row.cash)}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{fmtMoney(row.prizes)}</td>
                          <td className={`py-1.5 pl-2 text-right tabular-nums ${row.mismatch ? "text-rose-400 font-bold" : "text-muted-foreground"}`}>
                            {row.mismatch ? fmtMoneySigned(row.delta) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 gap-4">
              <Card className="border-border/50" data-testid="cc-coupon-card">
                <CardHeader className="pb-2">
                  <CardTitle className="font-mono text-sm flex items-center gap-2">
                    <Gift className="w-4 h-4 text-blue-400" />
                    Credit Card & Coupon Log
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!data?.credit_coupon_log?.length ? (
                    <p className="font-mono text-xs text-muted-foreground text-center py-4">No CC/Coupon entries in range</p>
                  ) : (
                    <ul className="space-y-1 max-h-48 overflow-y-auto">
                      {data.credit_coupon_log.map((row) => (
                        <li key={row.date} className="flex items-center justify-between gap-2 text-xs font-mono border-b border-border/20 py-1">
                          <button
                            type="button"
                            onClick={() => onJumpToDate?.(row.date)}
                            className="text-card-foreground hover:underline"
                            data-testid={`cc-row-${row.date}`}
                          >
                            {format(parseISO(row.date), "MMM dd")}
                          </button>
                          <span className="ml-auto text-blue-400">CC {fmtMoney(row.credit_card)}</span>
                          <span className="text-purple-400">Coup {fmtMoney(row.coupon)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border/50" data-testid="mismatch-card">
                <CardHeader className="pb-2">
                  <CardTitle className="font-mono text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-400" />
                    Mismatch Days
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!data?.mismatch_rows?.length ? (
                    <p className="font-mono text-xs text-muted-foreground text-center py-4">All clear — no mismatches</p>
                  ) : (
                    <ul className="space-y-1 max-h-48 overflow-y-auto">
                      {data.mismatch_rows.map((row) => (
                        <li key={row.date} className="flex items-center justify-between gap-2 text-xs font-mono border-b border-border/20 py-1">
                          <button
                            type="button"
                            onClick={() => onJumpToDate?.(row.date)}
                            className="text-card-foreground hover:underline"
                            data-testid={`mismatch-row-${row.date}`}
                          >
                            {format(parseISO(row.date), "MMM dd")}
                          </button>
                          <span className="ml-auto text-muted-foreground">Cash {fmtMoney(row.cash)}</span>
                          <span className="text-muted-foreground">vs {fmtMoney(row.cash_in_hand)}</span>
                          <span className={row.delta < 0 ? "text-rose-400 font-bold" : "text-emerald-400 font-bold"}>
                            {fmtMoneySigned(row.delta)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* History Totals — Weekly / Monthly / Yearly / Custom */}
          <HistoryTotals
            onJumpToRange={(rs, re) => {
              setPreset("custom");
              setRange({ start: rs, end: re });
            }}
          />
        </>
      )}
    </div>
  );
}
